import { LocationGroupKey } from "./location";
import { Brand, toBrand } from "@/offering_v2/brand";
export interface PartnerCatalog {
  partner: Partner;
  /** The brand this catalog's landing page paints, when it is not the partner's own.
   * A location in view that names its own brand still overrides it. */
  brand?: Brand;
  id: string;
  slug?: string;
  promoCode?: string;
  title: string;
  pageTitle?: string;
  dashboardCatalogName?: string;
  description: string;
  template?: string;
  partnerCatalogCategories: PartnerCatalogCategory[];
  partnerCatalogUpsell: PartnerCatalogUpsell;
  catalogBlocks: PartnerCatalogBlock[];
  showLocationFilter: boolean;
  /** What the catalog's location filter groups by. */
  locationFilterGroupBy: CatalogLocationFilterGroupBy;
  suppressDiscountPopup?: boolean;
}

export type CatalogLocationFilterGroupBy = Extract<
  LocationGroupKey,
  "city" | "neighborhood"
>;

export interface PartnerCatalogBlock {
  id: string;
  parentType: string;
  parentId: string;
  itemType: string;
  itemId: string;
  item: PartnerCatalogCategory | PartnerCatalogUpsell | null;
}

export interface PartnerCatalogCategory {
  id: string;
  title: string;
  description: string;
  cover: string;
  slug: string;
  ctaLabel?: string;
  catalogPageLayout: string;
  catalogOfferings?: CatalogOffering[];
  partnerCatalog?: PartnerCatalog;
  imageSrc?: string;
  catalogBlocks: PartnerCatalogBlock[];
  upsells: PartnerCatalogUpsell[];
  groupLandingPageHeaderDescription?: string;
  exploreMoreHeader?: string;
  offeringCardsHeader?: string;
}

export interface CatalogOffering {
  image: string;
  title: string;
  description: string;
  longDescription?: string;
  slug: string;
  comingSoon: boolean;
  membership: boolean;
  targetUrl?: string;
  multiPassOffering: boolean;
  price: string;
  currency: string;
  id: string;
  partnerOfferingId: string;
  locationIDs: string[];
  ctaLabel?: string;
}

export type CatalogUpsellTextAlign = "start" | "center" | "end" | "justify";

export interface CatalogUpsellVersionedImage {
  large: { url: string };
  medium: { url: string };
  original: { url: string };
  small: { url: string };
  thumb: { url: string };
  xlarge: { url: string };
}

export interface PartnerCatalogUpsell {
  id: string;
  image: string;
  title: string;
  description: string;
  longDescription?: string;
  learnMoreURL: string;
  ctaLabel?: string;
  layout: "left_to_right" | "right_to_left" | "default";
  textAlign: CatalogUpsellTextAlign;
  versionedImage?: CatalogUpsellVersionedImage;
  openNewWindow?: boolean;
}

export interface Partner {
  id: string;
  logo: string;
  name: string;
  internalName: string;
  partnerSlug: string;
  homepageURL: string;
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  linkedin_url?: string;
  tiktok_url?: string;
}

function toPartner(data: any): Partner {
  return {
    id: data.id,
    logo: data.logo,
    name: data.name,
    internalName: data.internal_name,
    partnerSlug: data.partner_slug,
    homepageURL: data.homepage_url,
    facebook_url: data.facebook_url,
    instagram_url: data.instagram_url,
    twitter_url: data.twitter_url,
    linkedin_url: data.linkedin_url,
    tiktok_url: data.tiktok_url,
  };
}

// The public catalog hides an offering whose listing spaces are all inactive, because its product
// page renders empty. The dashboard is the one surface where that state can be cured — activating a
// space is what gives the offering its inventory back — so dashboard callers opt out of the filter.
// Hiding the remedy behind the symptom leaves the partner no way back.
export interface CatalogMapOptions {
  includeUnbookableOfferings?: boolean;
}

export function toPartnerCatalog(
  data: any,
  options: CatalogMapOptions = {}
): PartnerCatalog {
  return {
    partner: toPartner(data.partner),
    brand: toBrand(data.brand),
    id: data.id,
    pageTitle: data.page_title,
    title: data.title,
    dashboardCatalogName: data.dashboard_catalog_name,
    slug: data.slug,
    promoCode: data.promo_code,
    description: data.description,
    template: data.template,
    catalogBlocks: data?.partner_catalog_blocks?.map((b: any) =>
      toCatalogBlock(b, options)
    ),
    partnerCatalogCategories: data.partner_catalog_categories
      .filter((c: any) => {
        const isUseGroup =
          data.template === "flexible" || data.template === "original";
        if (isUseGroup) {
          return !!c.catalog_offerings?.length;
        }

        return true;
      })
      .map((c: any) => toPartnerCatalogCategory(c, options)),
    partnerCatalogUpsell:
      data.partner_catalog_upsell &&
      toPartnerCatalogUpsell(data.partner_catalog_upsell),
    showLocationFilter: data.show_location_filter,
    locationFilterGroupBy:
      data.location_filter_group_by === "neighborhood"
        ? "neighborhood"
        : "city",
    suppressDiscountPopup: !!data.suppress_discount_popup,
  };
}

export function toPartnerCatalogCategory(
  data: any,
  options: CatalogMapOptions = {}
): PartnerCatalogCategory {
  const catalogOfferings = options.includeUnbookableOfferings
    ? data.catalog_offerings
    : data.catalog_offerings?.filter(hasBookableInventoryOrComingSoon);

  return {
    id: data.id,
    title: data.title || "",
    description: data.description || "",
    cover: data.cover_photo?.url || "",
    slug: data.slug || "",
    ctaLabel: data.cta_label,
    groupLandingPageHeaderDescription:
      data.group_landing_page_header_description,
    exploreMoreHeader: data.explore_more_header,
    catalogPageLayout: data.catalog_page_layout || "default",
    catalogOfferings: catalogOfferings?.map(toCatalogOffering),
    partnerCatalog: data.partner_catalog
      ? toPartnerCatalog(data.partner_catalog, options)
      : undefined,
    imageSrc: data.cover_photo ? data?.cover_photo : "",
    catalogBlocks: data?.partner_catalog_blocks?.map((b: any) =>
      toCatalogBlock(b, options)
    ),
    upsells: data?.partner_catalog_upsells?.map((u: any) =>
      toPartnerCatalogUpsell(u)
    ),
    offeringCardsHeader: data.offering_cards_header,
  };
}

// Catalog offerings with zero active listing spaces render a broken product page
// (no images, empty location dropdown). Hide them unless the partner has
// explicitly marked the offering as Coming Soon, which disables the CTA anyway.
export function hasBookableInventoryOrComingSoon(data: {
  coming_soon?: boolean;
  location_ids?: string[];
}): boolean {
  if (data.coming_soon) return true;
  return (data.location_ids?.length ?? 0) > 0;
}

export function normalizeCardDescription(description?: string | null) {
  return description?.replace(/[ \t]*\r?\n\s*/g, "\n").trim() ?? "";
}

function toCatalogOffering(data: any): CatalogOffering {
  return {
    image: data.image,
    title: data.title,
    description: normalizeCardDescription(data.description),
    slug: data.slug,
    comingSoon: data.coming_soon,
    membership: data.membership,
    targetUrl: data.target_url,
    ctaLabel: data.cta_label,
    multiPassOffering: data.multi_pass_offering, // only use for V1
    price: data.price || "",
    currency: data.currency || "",
    id: data.id || "",
    partnerOfferingId: data.partner_offering_id || "",
    longDescription: data.long_description,
    locationIDs: data.location_ids || [],
  };
}

function toPartnerCatalogUpsell(data: any): PartnerCatalogUpsell {
  return {
    id: data.id,
    image: data.image,
    title: data.title,
    description: data.description,
    learnMoreURL: data.learn_more_url,
    longDescription: data.long_description,
    layout: data.layout || "default",
    ctaLabel: data.cta_label,
    textAlign: data.text_align,
    versionedImage: data.versioned_image,
    openNewWindow: data.open_new_window,
  };
}

function toCatalogBlock(
  data: any,
  options: CatalogMapOptions = {}
): PartnerCatalogBlock {
  const {
    id,
    parentable_id: parentId,
    parentable_type: parentType,
    itemable_id: itemId,
    itemable_type: itemType,
    item: unionItem,
  } = data;

  let item;
  if (itemType === "PartnerCatalogCategory") {
    item = toPartnerCatalogCategory(unionItem, options);
  } else if (itemType === "PartnerCatalogUpsell") {
    item = toPartnerCatalogUpsell(unionItem);
  } else {
    item = null;
  }

  return {
    id,
    parentType,
    parentId,
    itemType,
    itemId,
    item,
  };
}

export function isPartnerCatalogCategory(
  item: PartnerCatalogCategory | PartnerCatalogUpsell | null
): item is PartnerCatalogCategory {
  return (
    item !== null &&
    "slug" in item &&
    "catalogOfferings" in item &&
    "catalogBlocks" in item
  );
}
